import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A single beat in the demo. The cursor tweens to `selector`'s centre, then
 * performs `action` (click → primary ripple, rightclick → red ripple,
 * dblclick → two ripples, press/release → grabs/releases for drag demos,
 * hover → no ripple). `label` is drawn next to the cursor during the hold.
 * `hold` is ms to pause after arrival (default: 500 with an action, 150
 * without).
 */
export type DemoStep = {
  selector: string;
  action?: 'click' | 'rightclick' | 'dblclick' | 'press' | 'release' | 'hover';
  label?: string;
  hold?: number;
  /**
   * When true, ALSO dispatch a real DOM event at the cursor position in
   * addition to the visual ripple. Use this on "opener" gestures (right-
   * click to open a context menu, click to open a dropdown) so the next
   * demo step's target actually exists in the DOM. Any menu opened via
   * trigger is closed automatically on demo cleanup via an Escape keydown.
   */
  trigger?: boolean;
};

interface Props {
  steps: DemoStep[];
  /** Total duration including holds; the engine fits travel into the rest. */
  durationMs?: number;
  loop?: boolean;
}

type Pt = { x: number; y: number };
type Box = { x: number; y: number; w: number; h: number };

// Reuse ::eq(N) parsing semantics from AnimatedHighlight
function resolveEl(selector: string): HTMLElement | null {
  let sel = selector;
  let index = 0;
  const m = /::eq\((\d+)\)\s*$/.exec(selector);
  if (m) {
    index = parseInt(m[1], 10);
    sel = selector.slice(0, m.index);
  }
  const els = document.querySelectorAll<HTMLElement>(sel);
  return els[index] ?? null;
}

function resolveCenter(selector: string): Pt | null {
  const el = resolveEl(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

function resolveBox(selector: string): Box | null {
  const el = resolveEl(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

function ease(t: number): number {
  // easeInOutCubic
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

interface Ripple {
  id: number;
  x: number;
  y: number;
  variant: 'click' | 'rightclick';
  startedAt: number;
}

const RIPPLE_MS = 650;

/**
 * Animated mouse cursor that drives "Show me how" demos. Purely visual — it
 * does not interact with the page. Teaches gestures (click, right-click,
 * drag) by showing where to aim and what action to perform.
 */
export function DemoCursor({ steps, durationMs = 4500, loop = false }: Props) {
  const [pos, setPos] = useState<Pt | null>(null);
  const [pressed, setPressed] = useState(false);
  const [label, setLabel] = useState<string | null>(null);
  const [ripples, setRipples] = useState<Ripple[]>([]);
  // Highlight box that wraps whichever element the cursor is currently
  // "near" — gives the user a clear visual anchor that moves with the
  // demo (block A → port → block B → port).
  const [focusBox, setFocusBox] = useState<Box | null>(null);
  const rafRef = useRef<number | null>(null);
  const rippleIdRef = useRef(0);

  useEffect(() => {
    if (steps.length === 0) return;

    const addRipple = (p: Pt, variant: 'click' | 'rightclick', now: number) => {
      const id = ++rippleIdRef.current;
      setRipples(prev => [...prev, { id, x: p.x, y: p.y, variant, startedAt: now }]);
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== id));
      }, RIPPLE_MS);
    };

    let didTrigger = false;

    const dispatchSyntheticMouseEvent = (p: Pt, type: 'click' | 'contextmenu' | 'dblclick') => {
      // elementFromPoint with retries — when a sibling element is being
      // animated in/out, the initial frame may return stale hits.
      const el = document.elementFromPoint(p.x, p.y) as HTMLElement | null;
      if (!el) return;
      const button = type === 'contextmenu' ? 2 : 0;
      el.dispatchEvent(new MouseEvent(type, {
        bubbles: true,
        cancelable: true,
        view: window,
        button,
        clientX: p.x,
        clientY: p.y,
      }));
    };

    const performAction = (step: DemoStep, p: Pt, now: number) => {
      setLabel(step.label ?? null);
      switch (step.action) {
        case 'click':
          addRipple(p, 'click', now);
          if (step.trigger) { dispatchSyntheticMouseEvent(p, 'click'); didTrigger = true; }
          break;
        case 'rightclick':
          addRipple(p, 'rightclick', now);
          if (step.trigger) { dispatchSyntheticMouseEvent(p, 'contextmenu'); didTrigger = true; }
          break;
        case 'dblclick':
          addRipple(p, 'click', now);
          setTimeout(() => addRipple(p, 'click', performance.now()), 180);
          if (step.trigger) {
            dispatchSyntheticMouseEvent(p, 'click');
            setTimeout(() => dispatchSyntheticMouseEvent(p, 'dblclick'), 50);
            didTrigger = true;
          }
          break;
        case 'press':
          setPressed(true);
          break;
        case 'release':
          setPressed(false);
          addRipple(p, 'click', now);
          break;
        case 'hover':
        default:
          break;
      }
    };

    // Plan timing
    const holds = steps.map(s => s.hold ?? (s.action && s.action !== 'hover' ? 500 : 150));
    const totalHoldMs = holds.reduce((a, b) => a + b, 0);
    const segments = Math.max(1, steps.length - 1);
    const travelMs = Math.max(500, durationMs - totalHoldMs);
    const perSegMs = travelMs / segments;

    // Arrival times for each step index
    const arrivals: number[] = [0];
    for (let i = 1; i < steps.length; i++) {
      arrivals.push(arrivals[i - 1] + holds[i - 1] + perSegMs);
    }
    const total = arrivals[arrivals.length - 1] + holds[holds.length - 1];

    let cancelled = false;
    let startTs: number | null = null;
    const firedActions = new Set<number>();
    let lastLoopCycle = 0;

    const tick = (ts: number) => {
      if (cancelled) return;
      if (startTs == null) startTs = ts;
      let elapsed = ts - startTs;

      if (loop && elapsed >= total) {
        // Reset for next loop
        const cyclesComplete = Math.floor(elapsed / total);
        if (cyclesComplete !== lastLoopCycle) {
          firedActions.clear();
          lastLoopCycle = cyclesComplete;
          setPressed(false);
        }
        elapsed = elapsed % total;
      } else if (!loop && elapsed >= total) {
        // Clamp at end
        elapsed = total;
      }

      // Find current segment
      let segIdx = 0;
      for (let i = 0; i < steps.length - 1; i++) {
        if (elapsed >= arrivals[i] && elapsed < arrivals[i + 1]) { segIdx = i; break; }
        if (elapsed >= arrivals[steps.length - 1]) { segIdx = steps.length - 1; break; }
      }

      const from = resolveCenter(steps[segIdx].selector);
      const fromBox = resolveBox(steps[segIdx].selector);
      const nextIdx = Math.min(steps.length - 1, segIdx + 1);
      const to = resolveCenter(steps[nextIdx].selector);
      const toBox = resolveBox(steps[nextIdx].selector);

      if (from && to) {
        const holdEnd = arrivals[segIdx] + holds[segIdx];
        if (elapsed < holdEnd) {
          setPos(from);
          setFocusBox(fromBox);
          // Fire action on first frame we land in this hold window
          const key = (lastLoopCycle * 1000) + segIdx;
          if (!firedActions.has(key)) {
            firedActions.add(key);
            performAction(steps[segIdx], from, ts);
          }
        } else {
          const segElapsed = elapsed - holdEnd;
          const t = Math.min(1, segElapsed / perSegMs);
          const eased = ease(t);
          setPos({
            x: from.x + (to.x - from.x) * eased,
            y: from.y + (to.y - from.y) * eased,
          });
          // Snap the focus box to the target once we've travelled more than
          // halfway — gives the impression of the highlight "moving" to the
          // destination block as the cursor approaches.
          setFocusBox(eased > 0.5 && toBox ? toBox : fromBox);
        }
      } else if (from) {
        setPos(from);
        setFocusBox(fromBox);
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      // If any step opened a menu via a synthetic trigger, dismiss it with
      // an Escape keydown so the UI returns to a clean state when the demo
      // ends. User can then perform the gesture themselves.
      if (didTrigger) {
        document.dispatchEvent(new KeyboardEvent('keydown', {
          key: 'Escape', code: 'Escape', bubbles: true, cancelable: true,
        }));
      }
    };
  }, [steps, durationMs, loop]);

  if (!pos) return null;

  // Render into its own portal at document.body with a very high z-index so
  // the cursor + ripples are guaranteed visible regardless of what the
  // GuideOverlay is doing (e.g. `visibility: hidden` during native drags,
  // stacking-context traps, etc.).
  return createPortal(
    <div
      aria-hidden="true"
      className="pointer-events-none"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, overflow: 'visible' }}
    >
      {/* Focus box — travels with the cursor so the user sees which element
          the demo is "on" right now. Animates between boxes via CSS
          transitions when setFocusBox swaps targets. */}
      {focusBox && (
        <div
          style={{
            position: 'absolute',
            left: focusBox.x - 6,
            top: focusBox.y - 6,
            width: focusBox.w + 12,
            height: focusBox.h + 12,
            borderRadius: 14,
            border: '3px solid hsl(var(--primary))',
            boxShadow: '0 0 32px 6px hsl(var(--primary) / 0.55)',
            background: 'hsl(var(--primary) / 0.06)',
            transition: 'left 0.25s ease, top 0.25s ease, width 0.25s ease, height 0.25s ease',
          }}
        />
      )}
      {/* Ripples */}
      {ripples.map(r => {
        const age = (performance.now() - r.startedAt) / RIPPLE_MS;
        const scale = 0.4 + age * 1.6;
        const opacity = Math.max(0, 1 - age);
        const color = r.variant === 'rightclick' ? 'hsl(0 84% 60%)' : 'hsl(var(--primary))';
        return (
          <div
            key={r.id}
            style={{
              position: 'absolute',
              left: r.x,
              top: r.y,
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: -24,
                top: -24,
                width: 48,
                height: 48,
                borderRadius: '50%',
                border: `3px solid ${color}`,
                transform: `scale(${scale})`,
                opacity,
                transition: 'none',
                boxShadow: `0 0 16px 2px ${color}`,
              }}
            />
          </div>
        );
      })}

      {/* Cursor */}
      <svg
        width="28"
        height="32"
        viewBox="0 0 28 32"
        style={{
          position: 'absolute',
          left: pos.x - 2,
          top: pos.y - 2,
          transform: pressed ? 'scale(0.9)' : 'scale(1)',
          transition: 'transform 0.12s ease',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))',
        }}
      >
        <path
          d="M2 2 L2 22 L8 17 L12 26 L15 25 L11 16 L19 16 Z"
          fill="white"
          stroke="hsl(var(--foreground))"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>

      {/* Action label */}
      {label && (
        <div
          style={{
            position: 'absolute',
            left: pos.x + 22,
            top: pos.y + 8,
            background: 'hsl(var(--foreground))',
            color: 'hsl(var(--background))',
            padding: '3px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          }}
        >
          {label}
        </div>
      )}
    </div>,
    document.body,
  );
}
